import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, isStepupRequired } from "@/api/client";
import type { LandingDetail, LandingMetrics, Release } from "@/lib/types";
import { useApp } from "@/App";
import { PublishModal } from "@/components/PublishModal";
import { QrModal } from "@/components/QrModal";
import { DailyChart, Funnel, PrizeList } from "@/components/MetricsBits";

const METRIC_PERIODS = [
  { days: 7, label: "7 дней" },
  { days: 14, label: "14 дней" },
  { days: 30, label: "30 дней" },
  { days: 90, label: "90 дней" },
];

export function SiteDetail() {
  const { id } = useParams();
  const landingId = Number(id);
  const nav = useNavigate();
  const { toast } = useApp();

  const [site, setSite] = useState<LandingDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [staged, setStaged] = useState<Release | null>(null); // freshly uploaded test release
  const [log, setLog] = useState<string[]>([]);
  const [errorLog, setErrorLog] = useState<string>(""); // full build error for a failed release
  const [metrics, setMetrics] = useState<LandingMetrics | null>(null);
  const [metricDays, setMetricDays] = useState(7);
  const [phase, setPhase] = useState<"idle" | "uploading" | "ready" | "failed">("idle");
  const [showPublish, setShowPublish] = useState<null | { release: Release; mode: "promote" | "rollback" }>(null);
  const [showQr, setShowQr] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setSite(await api.getLanding(landingId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [landingId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.landingMetrics(landingId, metricDays).then(setMetrics).catch(() => setMetrics(null));
  }, [landingId, metricDays]);

  // Stream the build log for a staged release over SSE until it reports done.
  useEffect(() => {
    if (!staged) return;
    setLog([]);
    setErrorLog("");
    const es = new EventSource(api.buildLogUrl(landingId, staged.id));
    es.onmessage = (ev) => setLog((l) => [...l, ev.data]);
    es.addEventListener("done", async () => {
      es.close();
      // Pull the durable release record: status + the full stored build log, so a
      // failed build shows the exact error even on reconnect / after the buffer cleared.
      const rel = await api.getRelease(landingId, staged.id);
      if (rel.build_status === "ready") {
        setPhase("ready");
      } else {
        setPhase("failed");
        setErrorLog(rel.build_log ?? log.join("\n"));
      }
      setSite(await api.getLanding(landingId));
    });
    es.onerror = () => es.close();
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staged, landingId]);

  if (err) return <div className="wrap"><div className="err">{err}</div></div>;
  if (!site) return <div className="wrap"><div className="muted">Загрузка…</div></div>;

  const status = site.is_paused ? "pause" : site.prod_release_id ? "live" : "prep";
  const statusLabel = { pause: "На паузе", live: "Опубликован", prep: "Готовится" }[status];

  const onFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast("Нужен ZIP-файл со сборкой сайта.");
      return;
    }
    setPhase("uploading");
    try {
      const rel = await api.testDeploy(landingId, file);
      setStaged(rel);
    } catch (e) {
      setPhase("idle");
      toast(e instanceof ApiError ? String(e.message) : "Не удалось загрузить файл.");
    }
  };

  const doPublish = async (note: string) => {
    if (!showPublish) return;
    const { release, mode } = showPublish;
    try {
      if (mode === "promote") await api.promote(landingId, release.id, note);
      else await api.rollback(landingId, release.id, note);
      setShowPublish(null);
      setPhase("idle");
      setStaged(null);
      await load();
      toast(mode === "promote" ? "Готово! Клиенты уже видят новую версию." : "Готово! Вернули прошлую версию.");
    } catch (e) {
      if (isStepupRequired(e)) {
        // dev mode confirms via /stepup; OIDC mode bounces to /login?stepup=1
        try {
          await api.stepup();
          await doPublish(note);
        } catch {
          window.location.href = "/api/auth/login?stepup=1";
        }
        return;
      }
      toast(e instanceof ApiError ? String(e.message) : "Не получилось опубликовать.");
    }
  };

  const togglePause = async () => {
    try {
      if (site.is_paused) await api.resume(landingId);
      else await api.pause(landingId);
      await load();
    } catch (e) {
      if (isStepupRequired(e)) {
        await api.stepup().then(togglePause).catch(() => (window.location.href = "/api/auth/login?stepup=1"));
        return;
      }
      toast("Недостаточно прав для этого сайта.");
    }
  };

  const prodRelease = site.releases.find((r) => r.id === site.prod_release_id);
  const otherReleases = site.releases.filter((r) => r.build_status === "ready" && r.id !== site.prod_release_id);

  return (
    <div className="wrap">
      <button className="back" onClick={() => nav("/")}>← Все сайты</button>
      <div className="shead">
        <h1>{site.name}</h1>
        <span className={`pill ${status}`}><span className="pd" />{statusLabel}</span>
      </div>

      <div className="openrow">
        <a className="btn sm" href={`https://${site.test_domain}`} target="_blank" rel="noreferrer"
           title={site.test_release_id ? "" : "Тест ещё не загружен"}>
          Открыть тест ↗
        </a>
        {site.prod_release_id && (
          <a className="btn sm" href={`https://${site.prod_domain}`} target="_blank" rel="noreferrer">
            Открыть боевой сайт ↗
          </a>
        )}
        <button className="btn sm" onClick={() => setShowQr(true)}>🔳 QR-код</button>
      </div>

      {site.is_paused && (
        <div className="mb">🛠 Сайт сейчас на паузе — на боевом адресе посетители видят «Скоро вернёмся». Предпросмотр (тест) при этом продолжает работать.</div>
      )}

      <div className="nowline">
        <span className="em">👀</span>
        <span>
          {prodRelease ? (
            <>Сейчас клиенты видят версию <b>{prodRelease.version_label}</b>
              {site.prod_deployed_at && <> от <b>{fmtDate(site.prod_deployed_at)}</b></>}.</>
          ) : (
            <>Сайт ещё не опубликован — клиенты пока его не видят.</>
          )}
          {site.visitors_per_day != null && <> Каждый день его открывают около <b>{site.visitors_per_day}</b> чел.</>}
        </span>
      </div>

      {metrics && (metrics.visitors > 0 || metrics.game_starts > 0) && (
        <div className="metric-strip">
          <div className="m"><div className="v">{metrics.visitors.toLocaleString("ru-RU")}</div><div className="l">посетителей</div></div>
          <div className="m"><div className="v">{metrics.game_starts.toLocaleString("ru-RU")}</div><div className="l">сыграли</div></div>
          <div className="m"><div className="v">{metrics.prizes_won.toLocaleString("ru-RU")}</div><div className="l">выиграли приз</div></div>
          <div className="m"><div className="v">{Math.round(metrics.win_rate * 100)}%</div><div className="l">доля выигрышей</div></div>
        </div>
      )}

      {/* STEP 1 — upload */}
      <div className="card step">
        <div className="h"><span className="num">1</span><span className="t">Загрузите обновление</span></div>
        <p className="d">Перетащите сюда ZIP с новой версией сайта. Клиенты его пока не увидят — он попадёт на адрес для предпросмотра.</p>
        <div className="body">
          {phase === "idle" && (
            <div
              className="drop"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("over"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("over")}
              onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("over"); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
            >
              <span className="em">📦</span>
              <span className="big">Перетащите файл сюда</span>
              <div className="small">или нажмите, чтобы выбрать на компьютере</div>
            </div>
          )}
          <input ref={fileRef} type="file" accept=".zip" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />

          {phase === "uploading" && (
            <div className="progress">
              <div className="bar"><i style={{ width: "70%" }} /></div>
              {site.build_mode === "build" || log.length > 0 ? (
                <div className="logbox">{log.join("\n") || "Готовим…"}</div>
              ) : (
                <div className="row done"><span className="ck">⏳</span>Готовим страницу…</div>
              )}
            </div>
          )}

          {phase === "ready" && (
            <div className="ready" style={{ marginTop: 12 }}>
              <div className="t">✓ Готово!</div>
              <div className="d">Новая версия подготовлена. Посмотрите, всё ли на месте, прежде чем показывать клиентам.</div>
              <a className="btn sm" href={`https://${site.test_domain}`} target="_blank" rel="noreferrer">
                Посмотреть, как получилось ↗
              </a>
            </div>
          )}

          {phase === "failed" && (
            <div className="failed" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>❌ Не удалось подготовить версию</div>
              <div className="small" style={{ marginBottom: 12 }}>
                Что-то в новой сборке пошло не так. Скопируйте ошибку ниже и передайте разработчику —
                по ней он поймёт, в чём дело. Клиенты ничего не заметили: на сайте по-прежнему прошлая версия.
              </div>
              <div className="logbox" data-testid="build-error">{errorLog || "(нет деталей)"}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  className="btn sm primary"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(errorLog);
                      toast("Ошибка скопирована — отправьте её разработчику.");
                    } catch {
                      toast("Не удалось скопировать. Выделите текст вручную.");
                    }
                  }}
                >
                  📋 Скопировать ошибку
                </button>
                <button className="btn sm" onClick={() => { setPhase("idle"); setStaged(null); setErrorLog(""); }}>
                  Загрузить заново
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* STEP 2 — publish */}
      <div className={"card step" + (phase === "ready" && site.can_publish ? "" : " locked")}>
        <div className="h"><span className="num">2</span><span className="t">Опубликуйте для клиентов</span></div>
        <p className="d">
          {phase === "ready"
            ? "Всё проверили? Нажмите — и обновление увидят клиенты."
            : "Сначала загрузите и проверьте обновление выше — потом сможете опубликовать."}
        </p>
        <div className="body">
          <button
            className="btn primary big"
            disabled={phase !== "ready" || !staged || !site.can_publish}
            onClick={() => staged && setShowPublish({ release: staged, mode: "promote" })}
          >
            Опубликовать для клиентов
          </button>
          {!site.can_publish && (
            <div className="reassure">🔒 У вас нет прав публиковать этот сайт — обратитесь к руководителю.</div>
          )}
          <div className="reassure">↩ Не переживайте: прошлую версию всегда можно вернуть в один клик.</div>
        </div>
      </div>

      {/* rollback */}
      {otherReleases.length > 0 && (
        <div className="more">
          <h3>Вернуть прошлую версию</h3>
          <div className="card">
            {prodRelease && (
              <div className="vrow">
                <div className="vt"><b>{prodRelease.version_label}</b><div className="vd">сейчас опубликована · {prodRelease.uploaded_by}</div></div>
                <button className="btn sm" disabled>Сейчас активна</button>
              </div>
            )}
            {otherReleases.map((r) => (
              <div className="vrow" key={r.id}>
                <div className="vt"><b>{r.version_label}</b><div className="vd">{fmtDate(r.created_at)} · {r.uploaded_by}</div></div>
                <button
                  className="btn sm"
                  disabled={!site.can_publish}
                  onClick={() => setShowPublish({ release: r, mode: "rollback" })}
                >
                  Вернуть эту
                </button>
              </div>
            ))}
          </div>
          <p className="hint" style={{ marginTop: 8 }}>Если в новой версии что-то пошло не так — вернитесь к той, что работала. Это безопасно.</p>
        </div>
      )}

      {/* pause */}
      {site.can_pause && (
        <div className="more">
          <h3>Временно скрыть сайт</h3>
          <div className="card toggle-card">
            <div className="tx">
              <div className="tt">Поставить боевой сайт на паузу</div>
              <div className="small muted">На боевом адресе ({site.prod_domain}) посетители увидят «Скоро вернёмся». Предпросмотр (тест) продолжит работать. Включается и выключается мгновенно.</div>
            </div>
            <button className={"switch" + (site.is_paused ? " on" : "")} onClick={togglePause}><span className="k" /></button>
          </div>
        </div>
      )}

      {/* detailed stats */}
      <div className="more">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <h3 style={{ margin: 0 }}>Статистика сайта</h3>
          <div className="period">
            {METRIC_PERIODS.map((p) => (
              <button key={p.days} className={metricDays === p.days ? "on" : ""} onClick={() => setMetricDays(p.days)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {!metrics || (metrics.visitors === 0 && metrics.page_views === 0) ? (
          <div className="empty-note" style={{ marginTop: 12 }}>
            Пока нет данных за этот период. Как только клиенты начнут заходить и играть, здесь
            появятся посетители, прохождение игры и выданные призы.
          </div>
        ) : (
          <>
            <div className="metric-strip" style={{ marginTop: 12 }}>
              <div className="m"><div className="v">{metrics.game_finishes.toLocaleString("ru-RU")}</div><div className="l">доиграли до конца</div></div>
              <div className="m"><div className="v">{Math.round(metrics.play_rate * 100)}%</div><div className="l">из зашедших сыграли</div></div>
              <div className="m"><div className="v">{metrics.avg_attempts ?? "—"}</div><div className="l">попыток в среднем</div></div>
              <div className="m"><div className="v">{metrics.avg_active_s != null ? metrics.avg_active_s + " c" : "—"}</div><div className="l">на странице (актив.)</div></div>
            </div>

            <div className="msec">
              <h3>По дням</h3>
              <div className="card" style={{ padding: "16px 18px" }}>
                <DailyChart data={metrics.by_day} />
                <div className="legend">
                  <span><i style={{ background: "var(--amber)" }} /> посетители</span>
                  <span><i style={{ background: "var(--green)" }} /> доля выигравших приз</span>
                </div>
              </div>
            </div>

            <div className="msec">
              <h3>Воронка</h3>
              <div className="card" style={{ padding: "16px 18px" }}>
                <Funnel m={metrics} />
              </div>
            </div>

            <div className="msec">
              <h3>Выигранные призы</h3>
              <div className="card" style={{ padding: "16px 18px" }}>
                <PrizeList prizes={metrics.prizes} />
              </div>
            </div>
          </>
        )}
      </div>

      {showPublish && (
        <PublishModal
          domain={site.prod_domain}
          mode={showPublish.mode}
          onCancel={() => setShowPublish(null)}
          onConfirm={doPublish}
        />
      )}

      {showQr && (
        <QrModal
          slug={site.slug}
          prodDomain={site.prod_domain}
          testDomain={site.test_domain}
          prodReady={!!site.prod_release_id}
          testReady={!!site.test_release_id}
          onClose={() => setShowQr(false)}
        />
      )}
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}
